import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export default function GameReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    gameApi.get(`/game/auth/reset-info?token=${encodeURIComponent(token)}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/auth/reset-password", { token, password: pw });
      localStorage.setItem("bmm_game_token", data.token);
      toast.success("Password updated! Welcome back, " + data.player.display_name);
      navigate("/game", { replace: true });
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-10" data-testid="game-reset-page">
      <div className="mx-auto max-w-sm rounded-2xl border border-border bg-card p-6 shadow-xl">
        <div className="mb-3 flex items-center gap-2 text-primary">
          <KeyRound size={22} />
          <h1 className="font-display text-2xl font-bold">Reset Password</h1>
        </div>
        {state === "loading" && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Checking your reset link…</p>
        )}
        {state === "invalid" && (
          <div data-testid="reset-invalid">
            <p className="text-sm font-semibold text-destructive">This reset link is invalid or has expired.</p>
            <p className="mt-2 text-sm text-muted-foreground">Please request a new password reset from the login screen.</p>
            <button data-testid="reset-back-login" onClick={() => navigate("/game", { replace: true })}
              className="mt-4 h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground active:scale-95">Back to Login</button>
          </div>
        )}
        {state === "ready" && (
          <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
            <p className="text-sm text-muted-foreground">
              Resetting password for <span className="font-bold text-primary">{info.display_name}</span>
              {info.player_number ? <> — Player #{info.player_number}</> : null}
            </p>
            <p className="text-xs text-muted-foreground">{info.email}</p>
            <input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="New password (min 8 characters)" autoComplete="new-password"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
            <input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              placeholder="Confirm new password" autoComplete="new-password"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
            <button data-testid="reset-submit" type="submit" disabled={busy}
              className="h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">
              {busy ? "Updating…" : "Set New Password & Log In"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
